import { createFileRoute } from "@tanstack/react-router";
import { Wallet, Users, ShieldCheck, Bike, CheckCircle2, Clock, DollarSign } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useEffect } from "react";

export const Route = createFileRoute("/join-riders")({
  component: JoinRiders,
});

function JoinRiders() {
  useEffect(() => {
    document.title = "Become a Rider — 24Boda";
  }, []);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-border/40 bg-background/95 backdrop-blur supports-backdrop-filter:bg-background/60">
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <a href="/" className="flex items-center">
            <img src="/logo.png" alt="24Boda" className="h-10 w-auto" />
          </a>
          <nav className="hidden items-center gap-8 text-sm font-medium text-muted-foreground md:flex">
            <a href="/" className="transition-colors hover:text-foreground">
              Home
            </a>
            <a href="/about" className="transition-colors hover:text-foreground">
              About
            </a>
            <a href="/blog" className="transition-colors hover:text-foreground">
              Blog
            </a>
            <a href="/faq" className="transition-colors hover:text-foreground">
              FAQ
            </a>
            <a href="/join-riders" className="text-foreground">
              Riders
            </a>
          </nav>
          <a
            href="/#download"
            className="inline-flex h-10 items-center justify-center rounded-lg bg-primary px-6 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Download App
          </a>
        </div>
      </header>

      <main>
        {/* Hero */}
        <section className="border-b border-border bg-secondary/30 py-16 sm:py-24">
          <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-2 text-sm font-semibold text-primary">
              <Bike className="size-4" />
              Now Hiring Riders
            </div>
            <h1 className="text-4xl font-bold tracking-tight text-foreground sm:text-5xl lg:text-6xl">
              Earn Daily with 24Boda
            </h1>
            <p className="mx-auto mt-6 max-w-2xl text-lg text-muted-foreground">
              Join hundreds of riders earning up to UGX 50,000+ per day. Flexible schedule, daily
              payouts, and full support.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <Button size="lg">Apply Now</Button>
              <Button size="lg" variant="outline">
                Learn More
              </Button>
            </div>
          </div>
        </section>

        {/* Earnings */}
        <section className="py-16 sm:py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h2 className="text-3xl font-bold tracking-tight text-foreground">
                How Much Can You Earn?
              </h2>
              <p className="mx-auto mt-4 max-w-2xl text-lg text-muted-foreground">
                Your earnings depend on how many trips you complete. Here's what riders typically
                make:
              </p>
            </div>
            <div className="mt-12 grid gap-6 sm:grid-cols-3">
              <EarningCard
                level="Part-Time"
                trips="10-15 trips/day"
                earnings="UGX 20,000 - 30,000"
                hours="3-4 hours"
              />
              <EarningCard
                level="Full-Time"
                trips="25-35 trips/day"
                earnings="UGX 50,000 - 70,000"
                hours="6-8 hours"
                highlighted
              />
              <EarningCard
                level="Top Earner"
                trips="40+ trips/day"
                earnings="UGX 80,000+"
                hours="8-10 hours"
              />
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="border-y border-border bg-secondary/30 py-16 sm:py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <h2 className="text-center text-3xl font-bold tracking-tight text-foreground">
              Why Ride with 24Boda?
            </h2>
            <div className="mt-12 grid gap-8 md:grid-cols-2 lg:grid-cols-4">
              <BenefitCard
                icon={Wallet}
                title="Daily Payouts"
                description="Get paid every day, no waiting. Withdraw anytime to your mobile money."
              />
              <BenefitCard
                icon={DollarSign}
                title="Keep 80%"
                description="You keep 80% of every fare. No hidden deductions or surprise fees."
              />
              <BenefitCard
                icon={ShieldCheck}
                title="Insurance"
                description="Covered on every trip. Accident insurance and support when you need it."
              />
              <BenefitCard
                icon={Users}
                title="24/7 Support"
                description="Dispatcher team, rider community, and customer support always available."
              />
            </div>
          </div>
        </section>

        {/* Requirements */}
        <section className="py-16 sm:py-24">
          <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
            <div className="grid gap-12 lg:grid-cols-2">
              <div>
                <h2 className="text-3xl font-bold tracking-tight text-foreground">Requirements</h2>
                <ul className="mt-8 space-y-4">
                  <RequirementItem text="Valid national ID and driver's license" />
                  <RequirementItem text="Registered vehicle with visible plate number" />
                  <RequirementItem text="Smartphone with internet connection" />
                  <RequirementItem text="18 years or older" />
                  <RequirementItem text="Clean background check" />
                </ul>
              </div>
              <Card className="p-8">
                <h3 className="text-xl font-semibold text-foreground">Quick Approval</h3>
                <p className="mt-4 text-sm text-muted-foreground">
                  Most riders get approved within 24 hours. Register at our Kampala office with your
                  documents.
                </p>
                <div className="mt-6 space-y-3 text-sm">
                  <div className="flex items-center gap-3">
                    <Clock className="size-5 text-primary" />
                    <span className="text-muted-foreground">
                      <strong className="text-foreground">Step 1:</strong> Submit application (5
                      min)
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="size-5 text-primary" />
                    <span className="text-muted-foreground">
                      <strong className="text-foreground">Step 2:</strong> Document verification (1
                      day)
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Bike className="size-5 text-primary" />
                    <span className="text-muted-foreground">
                      <strong className="text-foreground">Step 3:</strong> Start earning immediately
                    </span>
                  </div>
                </div>
                <Button className="mt-8 w-full">Apply Now</Button>
              </Card>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-border bg-secondary/20 py-12">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center justify-center gap-4">
            <div className="flex items-center">
              <img src="/logo.png" alt="24Boda" className="h-8 w-auto" />
            </div>
            <div className="text-center text-sm text-muted-foreground">
              © {new Date().getFullYear()} 24Boda. All rights reserved. Kampala, Uganda.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

function EarningCard({
  level,
  trips,
  earnings,
  hours,
  highlighted = false,
}: {
  level: string;
  trips: string;
  earnings: string;
  hours: string;
  highlighted?: boolean;
}) {
  return (
    <Card
      className={`p-6 text-center ${highlighted ? "border-primary bg-primary/5 ring-2 ring-primary" : ""}`}
    >
      {highlighted && (
        <div className="mb-4 inline-block rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground">
          Most Popular
        </div>
      )}
      <h3 className="text-xl font-bold text-foreground">{level}</h3>
      <div className="mt-4 text-3xl font-bold text-primary">{earnings}</div>
      <div className="mt-2 text-sm text-muted-foreground">per day</div>
      <div className="mt-4 space-y-2 text-sm text-muted-foreground">
        <div>{trips}</div>
        <div>{hours}</div>
      </div>
    </Card>
  );
}

function BenefitCard({
  icon: Icon,
  title,
  description,
}: {
  icon: React.ElementType;
  title: string;
  description: string;
}) {
  return (
    <div className="text-center">
      <div className="mx-auto flex size-12 items-center justify-center rounded-lg bg-primary/10 text-primary">
        <Icon className="size-6" />
      </div>
      <h3 className="mt-4 text-lg font-semibold text-foreground">{title}</h3>
      <p className="mt-2 text-sm text-muted-foreground">{description}</p>
    </div>
  );
}

function RequirementItem({ text }: { text: string }) {
  return (
    <li className="flex items-start gap-3">
      <CheckCircle2 className="mt-0.5 size-5 shrink-0 text-primary" />
      <span className="text-muted-foreground">{text}</span>
    </li>
  );
}
